/* eslint-disable import/order */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc, getDocs,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type QueryConstraint,
  type QuerySnapshot,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { SubKreweApprovalTask } from '~/shared/models/tasks/SubKreweApprovalTask.js'
import type { SubKreweJoinRequestTask } from '~/shared/models/tasks/SubKreweJoinRequestTask.js'
import type { WithNotifications } from '~/shared/models/traits/WithNotifications.js'
import { TaskFactory } from '../shared/models/TaskFactory.js'
import type { Task, TaskFirestoreData, TaskStatus, TaskType } from '../shared/types/task.js'
import { hasAssignment, hasDeadline, hasNotifications, hasReferences } from '../shared/utils/traits.js'
import { useMembersStore } from './members.js'
import { useSubKrewesStore } from './subkrewes.js'

/**
 * Store for managing tasks data
 *
 * Tasks are created and managed through the task system, with various implementations
 * for different types of tasks. This store handles the persistence and state management
 * for all task types.
 */
export const useTasksStore = defineStore('tasks', {
  state: () => ({
    // Store tasks by document ID for O(1) lookups
    tasks: {} as Record<string, Task>,
    loading: false,
    error: null as string | null,
    activeSubscriptions: [] as (() => void)[],
    initialized: false,
  }),

  getters: {
    /**
     * Convert map to array for components that expect arrays
     */
    tasksArray(): Task[] {
      return Object.values(this.tasks)
    },

    /**
     * Get pending tasks
     */
    pendingTasks(): Task[] {
      return this.tasksArray.filter(task => task.status === 'pending')
    },

    /**
     * Get tasks assigned to a specific entity
     */
    tasksForEntity(): (entityId: string, entityType: 'member' | 'subkrewe') => Task[] {
      return (entityId: string, entityType: 'member' | 'subkrewe') => {
        return this.tasksArray.filter((task) => {
          // Check metadata for assignment information
          if (task.metadata) {
            return task.metadata.assignedToId === entityId
              && task.metadata.assignedToType === entityType
          }
          return false
        })
      }
    },

    /**
     * Get tasks by type
     */
    tasksByType(): (taskType: TaskType) => Task[] {
      return (taskType: TaskType) => {
        return this.tasksArray.filter(task => task.taskType === taskType)
      }
    },

    /**
     * Get task by ID
     */
    getTaskById(): (id: string) => Task | undefined {
      return (id: string) => {
        const path = `tasks/${id}`
        return this.tasks[path]
      }
    },
  },

  actions: {
    /**
     * Create a new task
     * @param task Task to create
     * @returns ID of the created task
     */
    async createTask(task: Task): Promise<string> {
      this.loading = true
      this.error = null

      try {
        // Convert to Firestore data
        const taskData = TaskFactory.createFirestoreData(task)

        // Save to Firestore
        const db = getFirestore()
        const docRef = await addDoc(collection(db, 'tasks'), taskData as { [x: string]: never })

        // Update the task with the ID
        task.id = docRef.id

        // Add to local state using $patch
        this.$patch((state) => {
          state.tasks[docRef.path] = task
        })

        // When the task is created successfully, check if we should notify
        if (task.id) {
          // Only send notification if not explicitly suppressed
          const shouldNotify = !task.metadata?.suppressCreationNotification

          if (shouldNotify && hasNotifications(task)) {
            // Existing notification code...
          }
        }

        return docRef.id
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error creating task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Fetch a task by ID
     * @param id Task ID to fetch
     * @returns The task if found, otherwise null
     */
    async fetchTask(id: string): Promise<Task | null> {
      this.loading = true
      this.error = null

      try {
        // Check if already in store
        const path = `tasks/${id}`
        if (this.tasks[path]) {
          return this.tasks[path]
        }

        const db = getFirestore()
        const docRef = doc(db, 'tasks', id)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          return null
        }

        const data = docSnap.data() as TaskFirestoreData
        data.id = docSnap.id
        const task = TaskFactory.createTaskFromFirestore(data)

        // Update local store using $patch and construct the path manually
        this.$patch((state) => {
          state.tasks[`tasks/${docSnap.id}`] = task
        })

        return task
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error fetching task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Update a task
     * @param task Task to update
     */
    async updateTask(task: Task): Promise<boolean> {
      this.loading = true
      this.error = null

      try {
        if (!task.id) {
          throw new Error('Task has no ID')
        }

        // Update timestamp
        task.updatedAt = new Date()

        // Convert to Firestore data
        const taskData = TaskFactory.createFirestoreData(task)

        // Update in Firestore - using a more precise type assertion
        const db = getFirestore()
        await updateDoc(doc(db, 'tasks', task.id), taskData as { [x: string]: never })

        // Update in local state
        this.$patch((state) => {
          const path = `tasks/${task.id}`
          state.tasks[path] = task
        })

        return true
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error updating task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Update a task's status
     * @param id Task ID
     * @param status New status
     */
    async updateTaskStatus(id: string, status: TaskStatus): Promise<boolean> {
      const task = this.getTaskById(id)
      if (!task) {
        throw new Error(`Task not found: ${id}`)
      }

      // Store the old status for notification
      const oldStatus = task.status

      // Update the status
      task.status = status
      task.updatedAt = new Date()

      // Set completedAt for completed tasks
      if (status === 'completed') {
        // Set completedAt either through the trait or directly if property exists
        if (hasDeadline(task)) {
          task.completedAt = new Date()
        }
        else if ('completedAt' in task) {
          (task as Task & { completedAt: Date }).completedAt = new Date()
        }
      }

      // Send notifications if task supports them
      if (hasNotifications(task)) {
        // Use explicit casting after type guard verification
        const notifiableTask = task as Task & WithNotifications

        // Determine who should receive the notification
        const recipients: string[] = []

        // If the task has an assigned member, notify them
        if (hasAssignment(task) && task.assignedToType === 'member' && typeof task.assignedToId === 'string') {
          recipients.push(task.assignedToId)
        }

        // If the task has references (like creator, watchers), notify them too
        if (hasReferences(task)) {
          // Use explicit casting for reference methods
          const referencesTask = task as Task & {
            getReferencesByType: (type: string) => EntityReference[]
          }

          // Get creator reference if it exists
          const creatorRefs = referencesTask.getReferencesByType('creator')
          for (const ref of creatorRefs) {
            if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
              recipients.push(ref.entityId)
            }
          }

          // Get watcher references if they exist
          const watcherRefs = referencesTask.getReferencesByType('watcher')
          for (const ref of watcherRefs) {
            if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
              recipients.push(ref.entityId)
            }
          }
        }

        // Send notifications to all recipients
        for (const recipientId of recipients) {
          await notifiableTask.notifyStatusChanged(recipientId, oldStatus, status)
        }
      }

      // Save the changes
      return await this.updateTask(task)
    },

    /**
     * Delete a task
     * @param id Task ID to delete
     */
    async deleteTask(id: string): Promise<boolean> {
      this.loading = true
      this.error = null

      try {
        const db = getFirestore()
        // Delete from Firestore
        await deleteDoc(doc(db, 'tasks', id))

        // Remove from local state
        this.$patch((state) => {
          const path = `tasks/${id}`

          delete state.tasks[path]
        })

        return true
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error deleting task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Ensure tasks are loaded
     */
    async ensureTasksLoaded(): Promise<void> {
      if (this.initialized && Object.keys(this.tasks).length > 0) {
        return
      }

      await this.fetchTasks()
      this.initialized = true
    },

    /**
     * Fetch tasks with optional filtering
     * @param filters Optional query constraints
     */
    async fetchTasks(filters: QueryConstraint[] = [], forceRefresh = false): Promise<Task[]> {
      this.loading = true
      this.error = null

      try {
        const db = getFirestore()
        // Create query with filters and default sorting
        const tasksQuery = query(
          collection(db, 'tasks'),
          ...filters,
          orderBy('createdAt', 'desc'),
        )

        // Execute query
        const querySnapshot = await getDocs(tasksQuery)

        // Process results using $patch for batch updates
        const tasksToAdd: Record<string, Task> = {}

        querySnapshot.forEach((doc) => {
          const data = doc.data() as TaskFirestoreData
          data.id = doc.id

          // Skip if we already have this task and forceRefresh is false
          if (!forceRefresh && this.tasks[doc.ref.path]) {
            tasksToAdd[doc.ref.path] = this.tasks[doc.ref.path]!
            return
          }

          const task = TaskFactory.createTaskFromFirestore(data)
          tasksToAdd[doc.ref.path] = task
        })

        // Update local state
        this.$patch((state) => {
          Object.assign(state.tasks, tasksToAdd)
        })

        return Object.values(tasksToAdd)
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : 'Unknown error'
        console.error('Error fetching tasks:', error)
        return []
      }
      finally {
        this.loading = false
      }
    },
    /**
    * Execute a specific action on a task
    * @param taskId ID of the task to execute action on
    * @param action Name of the action method to call
    * @param actionArgs Additional arguments to pass to the action method
    * @returns True if action was successful
    */
    async executeTaskAction(taskId: string, action: string, ...actionArgs: unknown[]): Promise<boolean> {
      this.loading = true
      this.error = null

      try {
        // Get the task
        const task = await this.fetchTask(taskId)
        if (!task) {
          throw new Error(`Task not found: ${taskId}`)
        }

        // Check if the task has the requested action method
        if (typeof task[action] !== 'function') {
          throw new Error(`Task does not support action: ${action}`)
        }

        // Store old status for notification
        const oldStatus = task.status

        // Call the action method using function indexing AND pass through the arguments
        const actionFn = task[action] as (...args: unknown[]) => Promise<boolean>
        const result = await actionFn.call(task, ...actionArgs)

        if (result) {
          // If the action was successful and changed the status, send notification
          if (hasNotifications(task) && task.status !== oldStatus) {
            // Use explicit casting after type guard verification
            const notifiableTask = task as Task & WithNotifications

            // Determine who should receive notifications
            const recipients: string[] = []

            // If the task has an assigned member, notify them
            if (hasAssignment(task) && task.assignedToType === 'member' && typeof task.assignedToId === 'string') {
              recipients.push(task.assignedToId)
            }

            // If the task has references (like creator, watchers), notify them too
            if (hasReferences(task)) {
              // Use explicit casting for reference methods
              const referencesTask = task as Task & {
                getReferencesByType: (type: string) => EntityReference[]
              }

              // Get creator reference if it exists
              const creatorRefs = referencesTask.getReferencesByType('creator')
              for (const ref of creatorRefs) {
                if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
                  recipients.push(ref.entityId)
                }
              }

              // Get watcher references if they exist
              const watcherRefs = referencesTask.getReferencesByType('watcher')
              for (const ref of watcherRefs) {
                if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
                  recipients.push(ref.entityId)
                }
              }
            }

            // Send notifications to all recipients
            for (const recipientId of recipients) {
              await notifiableTask.notifyStatusChanged(recipientId, oldStatus, task.status)
            }
          }

          // Update the task in Firestore
          await this.updateTask(task)
        }

        return result
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : 'Unknown error'
        console.error(`Error executing task action ${action}:`, error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
    * Fetch tasks by implementation type and criteria
    * @param taskImplementation Implementation type of tasks to fetch
    * @param filters Additional filter criteria
    * @returns Array of matching tasks
    */
    async fetchTasksByType(
      taskImplementation: string,
      filters: {
        entityId?: string
        entityType?: 'member' | 'subkrewe'
        status?: TaskStatus | TaskStatus[]
        metadata?: Record<string, unknown>
      } = {},
      forceRefresh = false,
    ): Promise<Task[]> {
      // Build query constraints
      const queryConstraints: QueryConstraint[] = [
        where('taskImplementation', '==', taskImplementation),
      ]

      // Add status filter if provided
      if (filters.status) {
        if (Array.isArray(filters.status)) {
          queryConstraints.push(where('status', 'in', filters.status))
        }
        else {
          queryConstraints.push(where('status', '==', filters.status))
        }
      }

      // Add entity assignment filter if provided
      if (filters.entityId && filters.entityType) {
        queryConstraints.push(where('assignedToId', '==', filters.entityId))
        queryConstraints.push(where('assignedToType', '==', filters.entityType))
      }

      // Add metadata filters if provided
      if (filters.metadata) {
        Object.entries(filters.metadata).forEach(([key, value]) => {
          queryConstraints.push(where(`metadata.${key}`, '==', value))
        })
      }

      // Execute the query
      return await this.fetchTasks(queryConstraints, forceRefresh)
    },

    /**
     * Fetch tasks assigned to a specific entity
     * @param entityId Entity ID
     * @param entityType Entity type
     */
    async fetchTasksForEntity(entityId: string, entityType: 'member' | 'subkrewe'): Promise<Task[]> {
      const filters = [
        where('metadata.assignedToId', '==', entityId),
        where('metadata.assignedToType', '==', entityType),
      ]

      return await this.fetchTasks(filters)
    },

    /**
    * Subscribe to tasks for a specific entity with role-based filtering
    * @param entityId Entity ID
    * @param entityType Entity type
    * @param options Additional subscription options
    * @returns Function to unsubscribe
    */
    subscribeToEntityTasks(
      entityId: string,
      entityType: 'member' | 'subkrewe',
      options: {
        roleFiltering?: boolean // Whether to apply role-based filtering
        includeCompleted?: boolean // Whether to include completed tasks
        notifyOnChanges?: boolean // Whether to create notifications for task changes
        category?: string // For subscription tracking
      } = {},
    ): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) return null

      const { registerSubscription, hasSubscriptionWithKey, clearSubscriptionByKey } = useSubscriptions()
      const db = useFirestore()

      // Default options
      const includeCompleted = options.includeCompleted === true
      const category = options.category || `tasks-${entityType}-${entityId}`

      // Create a unique key for this subscription
      const subKey = `tasks-entity-${entityType}-${entityId}-${includeCompleted ? 'with-completed' : 'pending-only'}`

      // Check if we already have this subscription
      if (hasSubscriptionWithKey(subKey)) {
        console.warn(`[Tasks] Subscription to entity tasks already exists: ${entityType} ${entityId}`)
        // Instead of returning null, clear the existing subscription to prevent duplicates
        clearSubscriptionByKey(subKey)
      }

      // Create base query constraints
      const queryConstraints: QueryConstraint[] = [
        where('assignedToId', '==', entityId),
        where('assignedToType', '==', entityType),
      ]

      // Add status filter if not including completed tasks
      if (!includeCompleted) {
        queryConstraints.push(where('status', 'in', ['pending', 'in_progress']))
      }

      // Create the query
      const tasksQuery = query(
        collection(db, 'tasks'),
        ...queryConstraints,
        orderBy('createdAt', 'desc'),
      )

      // Set up the subscription
      const unsubscribe = onSnapshot(tasksQuery,
        snapshot => this.handleTaskSnapshot(snapshot, {
          notifyOnChanges: options.notifyOnChanges,
        }),
        (error) => {
          console.error(`[Tasks] Error in ${entityType} tasks subscription for ${entityId}:`, error)
        },
      )

      // Register the subscription
      registerSubscription(unsubscribe, category, subKey)

      return unsubscribe
    },

    /**
    * Subscribe to all tasks relevant to the current user based on their role
    * @param options Subscription options
    * @returns Array of unsubscribe functions
    */
    subscribeToUserRelevantTasks(options: {
      includeCompleted?: boolean
      notifyOnChanges?: boolean
    } = {}): Array<() => void> {
      const user = useCurrentUser().value
      if (!user?.uid) return []

      const membersStore = useMembersStore()
      const memberPath = `members/${user.uid}`
      const currentMember = membersStore.members[memberPath]

      if (!currentMember) {
        console.warn('[Tasks] Cannot setup relevant task subscriptions: Current member not found')
        return []
      }

      const unsubscribers: Array<(() => void) | null> = []

      // Subscribe to tasks assigned directly to the user
      const personalTasksUnsubscribe = this.subscribeToEntityTasks(
        user.uid,
        'member',
        {
          roleFiltering: false, // No need to filter user's own tasks
          includeCompleted: options.includeCompleted,
          notifyOnChanges: options.notifyOnChanges,
          category: 'tasks-personal',
        },
      )
      if (personalTasksUnsubscribe) unsubscribers.push(personalTasksUnsubscribe)

      // CRITICAL: For all user types, also subscribe to tasks where they are in metadata
      // This ensures join requests show up for the member
      const metadataTasksUnsubscribe = this.subscribeToTasksWithMetadata(
        'memberId',
        user.uid,
        {
          includeCompleted: options.includeCompleted,
          category: 'tasks-metadata',
        },
      )
      if (metadataTasksUnsubscribe) unsubscribers.push(metadataTasksUnsubscribe)

      // Other role-specific subscriptions...

      return unsubscribers.filter(Boolean) as Array<() => void>
    },

    subscribeToTasksWithMetadata(
      metadataField: string,
      metadataValue: string,
      options: {
        includeCompleted?: boolean
        category?: string
      } = {},
    ): (() => void) | null {
      const db = useFirestore()
      const { registerSubscription } = useSubscriptions()

      // Build constraints
      const queryConstraints = [
        where(`metadata.${metadataField}`, '==', metadataValue),
      ]

      // Add status filter if not including completed tasks
      if (!options.includeCompleted) {
        queryConstraints.push(where('status', 'in', ['pending', 'in_progress']))
      }

      // Create the query
      const tasksQuery = query(
        collection(db, 'tasks'),
        ...queryConstraints,
        orderBy('createdAt', 'desc'),
      )

      // Set up the subscription
      const unsubscribe = onSnapshot(tasksQuery,
        snapshot => this.handleTaskSnapshot(snapshot, { notifyOnChanges: false }),
        (error) => {
          console.error(`[Tasks] Error in metadata subscription for ${metadataField}=${metadataValue}:`, error)
        },
      )

      registerSubscription(unsubscribe, options.category || `tasks-metadata-${metadataField}`)
      return unsubscribe
    },

    /**
    * Subscribe to tasks of a specific type
    * @param taskType Task type to subscribe to
    * @param options Additional subscription options
    */
    subscribeToTaskType(
      taskType: TaskType,
      options: {
        status?: TaskStatus | TaskStatus[]
        includeCompleted?: boolean
        notifyOnChanges?: boolean
        category?: string
      } = {},
    ): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) return null

      const { registerSubscription } = useSubscriptions()
      const db = useFirestore()

      // Default options
      const includeCompleted = options.includeCompleted === true // Default to false
      const notifyOnChanges = options.notifyOnChanges === true // Default to false
      const category = options.category || `tasks-type-${taskType}`

      // Create query constraints
      const queryConstraints: QueryConstraint[] = [
        where('taskType', '==', taskType),
      ]

      // Add status filter if provided or not including completed tasks
      if (options.status) {
        if (Array.isArray(options.status)) {
          queryConstraints.push(where('status', 'in', options.status))
        }
        else {
          queryConstraints.push(where('status', '==', options.status))
        }
      }
      else if (!includeCompleted) {
        queryConstraints.push(where('status', 'in', ['pending', 'in_progress']))
      }

      // Create the query
      const tasksQuery = query(
        collection(db, 'tasks'),
        ...queryConstraints,
        orderBy('createdAt', 'desc'),
      )

      // Track if this is the initial load to avoid notifications for existing tasks
      let isInitialLoad = true

      // Set up the subscription
      const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
        // console.warn(`[Tasks] Snapshot update for type ${taskType} with ${snapshot.docChanges().length} changes`)

        // Process changes with a batched update
        const changes: Record<string, Task> = {}
        const removals: string[] = []
        const notifications: Array<{ taskId: string, type: 'created' | 'updated' | 'completed' }> = []

        snapshot.docChanges().forEach((change) => {
          const taskPath = `tasks/${change.doc.id}`

          if (change.type === 'added' || change.type === 'modified') {
            const data = change.doc.data() as TaskFirestoreData
            data.id = change.doc.id

            const task = TaskFactory.createTaskFromFirestore(data)
            changes[taskPath] = task

            // Track for notifications - but only if not the initial load
            if (notifyOnChanges && !isInitialLoad) {
              // Skip notification creation if suppressCreationNotification is set
              const shouldSuppressCreation = task.metadata?.suppressCreationNotification === true

              if (change.type === 'added' && !shouldSuppressCreation) {
                notifications.push({ taskId: task.id, type: 'created' })
              }
              else if (change.type === 'modified') {
                const oldTask = this.tasks[taskPath]
                if (oldTask && oldTask.status !== task.status) {
                  if (task.status === 'completed') {
                    notifications.push({ taskId: task.id, type: 'completed' })
                  }
                  else {
                    notifications.push({ taskId: task.id, type: 'updated' })
                  }
                }
              }
            }
          }
          else if (change.type === 'removed') {
            removals.push(taskPath)
          }
        })

        // Apply all changes in a single patch
        this.$patch((state) => {
          // Add or update tasks
          Object.entries(changes).forEach(([path, task]) => {
            state.tasks[path] = task
          })

          // Remove deleted tasks
          removals.forEach((path) => {
            delete state.tasks[path]
          })
        })

        // Handle notifications after state update
        if (notifyOnChanges && notifications.length > 0) {
          this.createTaskNotifications(notifications).catch((error) => {
            console.error('[Tasks] Error creating notifications:', error)
          })
        }

        // Mark initial load as complete
        isInitialLoad = false
      }, (error) => {
        console.error(`[Tasks] Error in subscription for type ${taskType}:`, error)
      })

      // Register the subscription
      registerSubscription(unsubscribe, category)

      return unsubscribe
    },

    /**
    * Create notifications for task changes
    * @param notifications Array of task notifications to create
    */
    async createTaskNotifications(
      notifications: Array<{ taskId: string, type: 'created' | 'updated' | 'completed' }>,
    ): Promise<void> {
      const user = useCurrentUser().value
      if (!user?.uid) return

      const notificationsStore = useNotificationsStore()

      for (const notification of notifications) {
        try {
          const task = this.tasks[`tasks/${notification.taskId}`]
          if (!task) continue

          // Skip tasks that handle their own notifications via WithNotifications trait
          // Those tasks should be handling their own notifications internally
          if (hasNotifications(task)) {
            continue
          }

          // Determine notification content based on type
          let title = ''
          let body = ''
          const category: NotificationCategory = 'task'

          switch (notification.type) {
            case 'created':
              title = 'New Task'
              body = `A new task has been assigned: ${task.title}`
              break
            case 'updated':
              title = 'Task Updated'
              body = `Task updated: ${task.title} (${task.status})`
              break
            case 'completed':
              title = 'Task Completed'
              body = `Task completed: ${task.title}`
              break
          }

          // Create the notification
          await notificationsStore.createNotification(user.uid, {
            title,
            body,
            category,
            linkType: 'task',
            linkTarget: task.id,
            priority: task.priority === 'critical' ? 'high' : 'normal',
            metadata: {
              taskId: task.id,
              taskType: task.taskType,
              taskStatus: task.status,
            },
          })
        }
        catch (error) {
          console.error('[Tasks] Error creating notification for task:', notification.taskId, error)
        }
      }
    },

    /**
     * Create a SubKrewe join request task
     * @param memberId The member requesting to join
     * @param subkreweId The SubKrewe they want to join
     * @param memberName Optional member name for display
     * @param subkreweName Optional SubKrewe name for display
     * @returns Task ID if successful
     */
    async createSubKreweJoinRequest(
      memberId: string,
      subkreweId: string,
      memberName?: string,
      subkreweName?: string,
    ): Promise<string> {
      // console.warn('Creating SubKrewe join request:', memberId, subkreweId)
      try {
        // Prepare stores up front
        const membersStore = useMembersStore()
        const subkrewesStore = useSubKrewesStore()

        let memberDetails: Member | undefined | null
        let subkreweDetails: SubKrewe | undefined | null

        // Fetch names if not provided
        if (!memberName || !subkreweName) {
          if (!memberName) {
            memberDetails = await membersStore.fetchMember(memberId)
            memberName = memberDetails?.name
          }

          if (!subkreweName) {
            subkreweDetails = await subkrewesStore.fetchSubKrewe(subkreweId)
            subkreweName = subkreweDetails?.name
          }
        }

        // If we still don't have member details, fetch to determine type metadata
        if (!memberDetails) {
          memberDetails = await membersStore.fetchMember(memberId)
        }

        // Create the task through TaskFactory
        const task = TaskFactory.createNewTask('subkrewe_join_request', {
          memberId,
          subkreweId,
          memberName,
          subkreweName,
        }) as SubKreweJoinRequestTask

        // Include member type, email, and phone in metadata for captain notifications
        try {
          const typeStr = String(memberDetails?.member_type || '').toLowerCase()
          const additionalMetadata: Record<string, unknown> = {}
          if (typeStr === 'redshirt') {
            additionalMetadata.memberType = 'redshirt'
          }
          if (memberDetails?.email) {
            additionalMetadata.applicantEmail = memberDetails.email
          }
          else {
            console.warn('createSubKreweJoinRequest: member email unexpectedly missing for', memberId)
          }
          if (memberDetails?.phone) {
            additionalMetadata.applicantPhone = memberDetails.phone
          }
          else {
            console.warn('createSubKreweJoinRequest: member phone unexpectedly missing for', memberId)
          }
          if (Object.keys(additionalMetadata).length > 0) {
            task.metadata = {
              ...task.metadata,
              ...additionalMetadata,
            }
          }
        }
        catch {
          // noop
        }

        // Save to Firestore and state
        const taskId = await this.createTask(task)
        // console.warn(hasNotifications(task))
        // Send notifications about the new application
        if (hasNotifications(task)) {
          try {
            await task.notifySubmitted()
            // console.warn('Join request notification sent successfully')
          }
          catch (error) {
            console.error('Error sending join request notification:', error)
            // Continue even if notification fails - don't rethrow
          }
        }

        return taskId
      }
      catch (error) {
        console.error('Error creating SubKrewe join request:', error)
        throw error
      }
    },

    /**
     * Process a SubKrewe join request (approve or deny)
     * @param taskId The task ID
     * @param approve Whether to approve or deny
     * @param actingMemberId ID of the member taking the action
     * @param reason Optional reason for denial
     * @returns Success status
     */
    async processSubKreweJoinRequest(
      taskId: string,
      approve: boolean,
      actingMemberId?: string,
      reason?: string,
    ): Promise<boolean> {
      try {
        const task = await this.fetchTask(taskId)
        if (!task) {
          throw new Error(`Task not found: ${taskId}`)
        }

        if (task.taskImplementation !== 'SubKreweJoinRequestTask') {
          throw new Error(`Expected SubKreweJoinRequestTask, got ${task.taskImplementation}`)
        }

        // Record who approved/denied the request
        if (actingMemberId) {
          if (approve) {
            task.metadata.approvedById = actingMemberId
          }
          else {
            task.metadata.deniedById = actingMemberId
          }
        }

        // Cast to access the specific methods
        const joinTask = task as unknown as {
          approve(): Promise<boolean>
          deny(reason?: string): Promise<boolean>
        }

        let success: boolean
        if (approve) {
          success = await joinTask.approve()
        }
        else {
          success = await joinTask.deny(reason)
        }

        // Update the task in our store
        if (success) {
          await this.updateTask(task)
        }

        return success
      }
      catch (error) {
        console.error('Error processing SubKrewe join request:', error)
        return false
      }
    },

    /**
     * Create a SubKrewe approval task
     * @param subkreweId The SubKrewe pending approval
     * @param subkreweName Optional name of the SubKrewe for display
     * @param captainId Optional id of the SubKrewe captain
     * @param captainName Optional name of the SubKrewe captain for display
     * @returns Task ID if successful
     */
    async createSubKreweApprovalTask(
      subkreweId: string,
      subkreweName?: string,
      captainId?: string,
      captainName?: string,
    ): Promise<string> {
      try {
        // Check if this SubKrewe is a band to determine task assignment
        const subkrewesStore = useSubKrewesStore()
        const subkrewe = await subkrewesStore.fetchSubKrewe(subkreweId)
        const isBand = subkrewe?.music === 'band' || subkrewe?.music === 'bring_band'

        // Get admin users to assign the task to
        const membersStore = useMembersStore()
        const adminUsers = membersStore.membersArray.filter(member => member.roles?.admin === true)

        // For band SubKrewes, also include band admins
        const bandAdminUsers = isBand
          ? membersStore.membersArray.filter(member => member.roles?.band_admin === true)
          : []

        // Combine admin and band admin users (remove duplicates)
        const allAdminUsers = [...adminUsers, ...bandAdminUsers.filter(bandAdmin =>
          !adminUsers.some(admin => admin.id === bandAdmin.id),
        )]

        if (allAdminUsers.length === 0) {
          throw new Error('No admin users found to assign SubKrewe approval task')
        }

        // Create a new approval task
        const task = new SubKreweApprovalTask(
          subkreweId,
          subkreweName || '',
          captainId || '',
          captainName || '',
        )

        // Set status to pending
        task.status = 'pending'

        // Assign task to the first admin user (primary assignee)
        // All admins will see it via the taskType='admin' subscription
        const primaryAdmin = allAdminUsers[0]
        if (!primaryAdmin?.id) {
          throw new Error('Primary admin user has invalid ID')
        }
        task.assign(primaryAdmin.id, 'member')

        // Add band info to metadata for proper task routing
        if (task.metadata && typeof task.metadata === 'object') {
          task.metadata.isBand = isBand
          // Store all admin user IDs who should see this task
          task.metadata.assignedAdminIds = allAdminUsers.map(admin => admin.id)
        }

        // Create the task in the database
        const taskId = await this.createTask(task)

        // Now handle notification - make sure the task has its ID set
        if (taskId) {
          task.id = taskId
          // Call notifySubmitted directly - this will notify admins (and band admins for bands)
          if (hasNotifications(task)) {
            await task.notifySubmitted()
          }
        }

        return taskId
      }
      catch (error) {
        console.error('Error creating SubKrewe approval task:', error)
        throw error
      }
    },

    /**
     * Process a SubKrewe approval task (approve or deny)
     * @param taskId The task ID
     * @param approve Whether to approve or deny
     * @param actingMemberId ID of the admin taking the action
     * @param reason Optional reason for denial
     * @returns Success status
     */
    async processSubKreweApprovalTask(
      taskId: string,
      approve: boolean,
      actingMemberId?: string,
      reason?: string,
    ): Promise<boolean> {
      try {
        // Fetch the task
        const task = await this.fetchTask(taskId) as SubKreweApprovalTask
        if (!task || task.taskImplementation !== 'SubKreweApprovalTask') {
          throw new Error('Not a valid SubKrewe approval task')
        }

        // Record who took the action if available
        if (actingMemberId) {
          if (approve) {
            task.metadata.approvedById = actingMemberId
          }
          else {
            task.metadata.deniedById = actingMemberId
          }
        }

        // Execute the appropriate action
        let success = false
        if (approve) {
          success = await task.approve()
        }
        else {
          success = await task.deny(reason)
        }

        // If the action succeeded, update the task in the database
        if (success) {
          await this.updateTask(task)
        }

        return success
      }
      catch (error) {
        console.error('Error processing SubKrewe approval task:', error)
        return false
      }
    },

    /**
     * Clear all active subscriptions
     */
    clearSubscriptions(): void {
      this.activeSubscriptions.forEach(unsubscribe => unsubscribe())
      this.activeSubscriptions = []
    },

    /**
     * Reset the store state
     */
    resetState(): void {
      this.clearSubscriptions()
      // Important: replace tasks object to truly clear keys (deep-merge with {} would keep existing keys)
      this.tasks = {}
      this.loading = false
      this.error = null
      this.initialized = false
    },

    /**
     * Handle snapshot updates for task subscriptions
     * @param snapshot Firestore snapshot
     * @param options Optional configuration for notification handling
     */
    handleTaskSnapshot(
      snapshot: QuerySnapshot,
      options: {
        notifyOnChanges?: boolean
      } = {},
    ): void {
      // Default options
      const notifyOnChanges = options.notifyOnChanges === true

      // Process changes with a batched update
      this.$patch((state) => {
        // Track which tasks we need to notify about
        const notifications: Array<{ taskId: string, type: 'created' | 'updated' | 'completed' }> = []

        snapshot.docChanges().forEach((change) => {
          const taskId = change.doc.id
          const taskPath = `tasks/${taskId}`

          if (change.type === 'added' || change.type === 'modified') {
            // Get data and convert to Task object
            const data = change.doc.data() as TaskFirestoreData

            try {
              // Create Task instance from Firestore data
              const task = TaskFactory.createTaskFromFirestore({
                ...data,
                id: taskId,
              })

              // Add to store
              state.tasks[taskPath] = task

              // Add to notifications queue if needed
              if (notifyOnChanges) {
                // Skip notification creation if suppressCreationNotification is set
                const shouldSuppressCreation = task.metadata?.suppressCreationNotification === true
                const isNewTask = change.type === 'added'

                // For new tasks, respect the suppressCreationNotification flag
                if (isNewTask) {
                  if (!shouldSuppressCreation) {
                    notifications.push({ taskId, type: 'created' })
                  }
                }
                // For updates, still create notifications
                else if (change.type === 'modified' && task.status === 'completed') {
                  notifications.push({ taskId, type: 'completed' })
                }
                else if (change.type === 'modified') {
                  notifications.push({ taskId, type: 'updated' })
                }
              }
            }
            catch (error) {
              console.error(`[Tasks] Error processing task ${taskId}:`, error)
            }
          }
          else if (change.type === 'removed') {
            // Remove from store if exists
            if (taskPath in state.tasks) {
              delete state.tasks[taskPath]
            }
          }
        })

        // Create notifications for changes if needed
        if (notifications.length > 0) {
          // Schedule notification creation in the next tick
          setTimeout(() => {
            this.createTaskNotifications(notifications)
          }, 0)
        }
      })
    },
  },
})
